import { useRef, useState, useEffect } from "react";

type Mode = "partial" | "full";

function getNearestScrollViewport(from: HTMLElement): HTMLElement | null {
  // Radix Themes ScrollArea viewport carries this data-attr
  const radixViewport = from.closest<HTMLElement>(
    "[data-radix-scroll-area-viewport]"
  );
  if (radixViewport) return radixViewport;

  // Fallback: nearest ancestor that actually scrolls
  let el: HTMLElement | null = from.parentElement;
  while (el) {
    const cs = getComputedStyle(el);
    const overflowY = cs.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return el;
    el = el.parentElement;
  }
  return null;
}

export function useVisibleInNearestScrollArea(opts?: {
  mode?: Mode;
  minRatio?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [ratio, setRatio] = useState(0);

  const mode = opts?.mode ?? "partial";
  const minRatio = opts?.minRatio ?? 0.1;

  useEffect(() => {
    const target = ref.current;
    if (!target) return;

    const root = getNearestScrollViewport(target);

    // --- IntersectionObserver path ---
    let io: IntersectionObserver | null = null;
    if ("IntersectionObserver" in window) {
      // Choose thresholds so we fire near the boundary we care about
      const thresholds = mode === "full" ? [1] : [0, minRatio, 1];

      io = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          setRatio(e.intersectionRatio);
          setInView(
            mode === "full"
              ? e.intersectionRatio >= 1
              : e.intersectionRatio >= minRatio
          );
        },
        { root, threshold: thresholds }
      );
      io.observe(target);
    }

    // --- Fallback (and ensures initial state) ---
    let rafId = 0;
    const compute = () => {
      if (!target) return;
      const viewportEl = root ?? document.documentElement; // window viewport
      const tRect = target.getBoundingClientRect();
      const vRect = viewportEl.getBoundingClientRect();

      const visiblePx =
        Math.min(tRect.bottom, vRect.bottom) - Math.max(tRect.top, vRect.top);
      const h = Math.max(1, tRect.height);
      const r = Math.max(0, visiblePx) / h;

      setRatio(r);
      setInView(mode === "full" ? r >= 1 : r >= minRatio);
    };

    const reflow = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(compute);
    };

    const scrollEl: HTMLElement | Window = root ?? window;
    const resizeEl: Window = window;

    // Always attach this minimal listener so state is correct immediately
    scrollEl.addEventListener("scroll", reflow, { passive: true });
    resizeEl.addEventListener("resize", reflow);
    reflow();

    return () => {
      io?.disconnect();
      scrollEl.removeEventListener("scroll", reflow as any);
      resizeEl.removeEventListener("resize", reflow);
      cancelAnimationFrame(rafId);
    };
  }, [mode, minRatio]);

  return { ref, inView, ratio };
}
